import type {Page, Result, ValidationError} from 'framework';
import type {
  CreateCustomExerciseInput,
  ListCatalogExercisesInput,
  UpdateCustomExerciseInput,
} from '../application/ports/exercise-catalog.repository';
import type {
  AddExerciseToWorkoutInput,
  CreateWorkoutTemplateInput,
  DeleteSetInput,
  LogSetInput,
  ListWorkoutTemplatesInput,
  RemoveExerciseFromWorkoutInput,
  ReorderWorkoutExercisesInput,
  ReplaceTemplateExercisesInput,
  ReplaceWorkoutExerciseInput,
  StartWorkoutFromTemplateInput,
  UpdateSetInput,
  UpdateWorkoutTemplateInput,
} from '../application/ports/workout.repository';
import type {AddExerciseToWorkout} from '../application/use-cases/add-exercise-to-workout';
import type {CompleteWorkout} from '../application/use-cases/complete-workout';
import type {CreateCustomExercise} from '../application/use-cases/create-custom-exercise';
import type {DeleteCustomExercise} from '../application/use-cases/delete-custom-exercise';
import type {GetCatalogExercise} from '../application/use-cases/get-catalog-exercise';
import type {CreateWorkoutTemplate} from '../application/use-cases/create-workout-template';
import type {DeleteSet} from '../application/use-cases/delete-set';
import type {DeleteWorkoutTemplate} from '../application/use-cases/delete-workout-template';
import type {DiscardWorkout} from '../application/use-cases/discard-workout';
import type {DuplicateWorkoutTemplate} from '../application/use-cases/duplicate-workout-template';
import type {GetActiveWorkout} from '../application/use-cases/get-active-workout';
import type {GetCompletedWorkoutSeries} from '../application/use-cases/get-completed-workout-series';
import type {GetWorkout} from '../application/use-cases/get-workout';
import type {GetFocusModePreference} from '../application/use-cases/get-focus-mode-preference';
import type {GetKeepScreenAwakePreference} from '../application/use-cases/get-keep-screen-awake-preference';
import type {GetWorkoutTemplate} from '../application/use-cases/get-workout-template';
import type {ListCatalogExercises} from '../application/use-cases/list-catalog-exercises';
import type {ListCatalogMuscles} from '../application/use-cases/list-catalog-muscles';
import type {ListWorkoutTemplates} from '../application/use-cases/list-workout-templates';
import type {LogSet} from '../application/use-cases/log-set';
import type {RemoveExerciseFromWorkout} from '../application/use-cases/remove-exercise-from-workout';
import type {ReplaceWorkoutExercise} from '../application/use-cases/replace-workout-exercise';
import type {ReorderWorkoutExercises} from '../application/use-cases/reorder-workout-exercises';
import type {ReplaceWorkoutTemplateExercises} from '../application/use-cases/replace-workout-template-exercises';
import type {SetFocusModePreference} from '../application/use-cases/set-focus-mode-preference';
import type {SetKeepScreenAwakePreference} from '../application/use-cases/set-keep-screen-awake-preference';
import type {StartWorkoutFromTemplate} from '../application/use-cases/start-workout-from-template';
import type {UpdateCustomExercise} from '../application/use-cases/update-custom-exercise';
import type {UpdateSet} from '../application/use-cases/update-set';
import type {UpdateWorkoutTemplate} from '../application/use-cases/update-workout-template';
import type {WorkoutSeriesRange} from '../application/ports/workout.repository';
import type {CatalogExercise} from '../domain/catalog-exercise';
import type {ExerciseId} from '../domain/exercise-id.value-object';
import type {FocusModePreference} from '../domain/focus-mode-preference.value-object';
import type {KeepScreenAwakePreference} from '../domain/keep-screen-awake-preference.value-object';
import type {CatalogMuscle} from '../domain/catalog-muscle';
import type {WorkoutError} from '../domain/errors';
import type {
  CompleteWorkoutResult,
  LogSetResult,
  ReplaceWorkoutExerciseResult,
  Workout,
} from '../domain/workout';
import type {WorkoutId} from '../domain/workout-id.value-object';
import type {WorkoutSummary} from '../domain/workout-summary';
import type {WorkoutTemplate, WorkoutTemplateSummary} from '../domain/workout-template';
import type {WorkoutTemplateId} from '../domain/workout-template-id.value-object';

export class WorkoutController {
  constructor(
    private readonly listWorkoutTemplates: ListWorkoutTemplates,
    private readonly getWorkoutTemplate: GetWorkoutTemplate,
    private readonly createWorkoutTemplate: CreateWorkoutTemplate,
    private readonly updateWorkoutTemplate: UpdateWorkoutTemplate,
    private readonly replaceWorkoutTemplateExercises: ReplaceWorkoutTemplateExercises,
    private readonly deleteWorkoutTemplate: DeleteWorkoutTemplate,
    private readonly duplicateWorkoutTemplate: DuplicateWorkoutTemplate,
    private readonly getActiveWorkout: GetActiveWorkout,
    private readonly getWorkout: GetWorkout,
    private readonly getCompletedWorkoutSeries: GetCompletedWorkoutSeries,
    private readonly startWorkoutFromTemplate: StartWorkoutFromTemplate,
    private readonly discardWorkout: DiscardWorkout,
    private readonly logSet: LogSet,
    private readonly updateSet: UpdateSet,
    private readonly deleteSet: DeleteSet,
    private readonly reorderWorkoutExercises: ReorderWorkoutExercises,
    private readonly removeExerciseFromWorkout: RemoveExerciseFromWorkout,
    private readonly completeWorkout: CompleteWorkout,
    private readonly addExerciseToWorkout: AddExerciseToWorkout,
    private readonly replaceWorkoutExercise: ReplaceWorkoutExercise,
    private readonly listCatalogExercises: ListCatalogExercises,
    private readonly listCatalogMuscles: ListCatalogMuscles,
    private readonly getCatalogExerciseUseCase: GetCatalogExercise,
    private readonly createCustomExerciseUseCase: CreateCustomExercise,
    private readonly updateCustomExerciseUseCase: UpdateCustomExercise,
    private readonly deleteCustomExerciseUseCase: DeleteCustomExercise,
    private readonly getFocusModePreferenceUseCase: GetFocusModePreference,
    private readonly setFocusModePreferenceUseCase: SetFocusModePreference,
    private readonly getKeepScreenAwakePreferenceUseCase: GetKeepScreenAwakePreference,
    private readonly setKeepScreenAwakePreferenceUseCase: SetKeepScreenAwakePreference,
  ) {}

  listTemplates(
    input: ListWorkoutTemplatesInput,
  ): Promise<Result<Page<WorkoutTemplateSummary>, WorkoutError>> {
    return this.listWorkoutTemplates.execute(input);
  }

  getTemplate(templateId: WorkoutTemplateId): Promise<Result<WorkoutTemplate, WorkoutError>> {
    return this.getWorkoutTemplate.execute({templateId});
  }

  createTemplate(
    input: CreateWorkoutTemplateInput,
  ): Promise<Result<WorkoutTemplate, WorkoutError | ValidationError>> {
    return this.createWorkoutTemplate.execute(input);
  }

  updateTemplate(
    input: UpdateWorkoutTemplateInput,
  ): Promise<Result<WorkoutTemplate, WorkoutError | ValidationError>> {
    return this.updateWorkoutTemplate.execute(input);
  }

  replaceTemplateExercises(
    input: ReplaceTemplateExercisesInput,
  ): Promise<Result<WorkoutTemplate, WorkoutError>> {
    return this.replaceWorkoutTemplateExercises.execute(input);
  }

  deleteTemplate(templateId: WorkoutTemplateId): Promise<Result<void, WorkoutError>> {
    return this.deleteWorkoutTemplate.execute({templateId});
  }

  duplicateTemplate(templateId: WorkoutTemplateId): Promise<Result<WorkoutTemplate, WorkoutError>> {
    return this.duplicateWorkoutTemplate.execute({templateId});
  }

  getActive(): Promise<Result<Workout | null, WorkoutError>> {
    return this.getActiveWorkout.execute();
  }

  getById(workoutId: WorkoutId): Promise<Result<Workout, WorkoutError>> {
    return this.getWorkout.execute({workoutId});
  }

  series(range: WorkoutSeriesRange): Promise<Result<readonly WorkoutSummary[], WorkoutError>> {
    return this.getCompletedWorkoutSeries.execute(range);
  }

  startFromTemplate(input: StartWorkoutFromTemplateInput): Promise<Result<Workout, WorkoutError>> {
    return this.startWorkoutFromTemplate.execute(input);
  }

  discard(workoutId: WorkoutId): Promise<Result<Workout, WorkoutError>> {
    return this.discardWorkout.execute({workoutId});
  }

  log(input: LogSetInput): Promise<Result<LogSetResult, WorkoutError | ValidationError>> {
    return this.logSet.execute(input);
  }

  updateLoggedSet(input: UpdateSetInput): Promise<Result<Workout, WorkoutError>> {
    return this.updateSet.execute(input);
  }

  deleteLoggedSet(input: DeleteSetInput): Promise<Result<Workout, WorkoutError>> {
    return this.deleteSet.execute(input);
  }

  reorderExercises(input: ReorderWorkoutExercisesInput): Promise<Result<Workout, WorkoutError>> {
    return this.reorderWorkoutExercises.execute(input);
  }

  removeExercise(input: RemoveExerciseFromWorkoutInput): Promise<Result<Workout, WorkoutError>> {
    return this.removeExerciseFromWorkout.execute(input);
  }

  complete(workoutId: WorkoutId): Promise<Result<CompleteWorkoutResult, WorkoutError>> {
    return this.completeWorkout.execute({workoutId});
  }

  addExercise(
    input: AddExerciseToWorkoutInput,
  ): Promise<Result<Workout, WorkoutError | ValidationError>> {
    return this.addExerciseToWorkout.execute(input);
  }

  replaceExercise(
    input: ReplaceWorkoutExerciseInput,
  ): Promise<Result<ReplaceWorkoutExerciseResult, WorkoutError>> {
    return this.replaceWorkoutExercise.execute(input);
  }

  listExercises(
    input: ListCatalogExercisesInput,
  ): Promise<Result<Page<CatalogExercise>, WorkoutError>> {
    return this.listCatalogExercises.execute(input);
  }

  listMuscles(): Promise<Result<readonly CatalogMuscle[], WorkoutError>> {
    return this.listCatalogMuscles.execute();
  }

  getCatalogExercise(id: ExerciseId): Promise<Result<CatalogExercise, WorkoutError>> {
    return this.getCatalogExerciseUseCase.execute(id);
  }

  createCustomExercise(
    input: CreateCustomExerciseInput,
  ): Promise<Result<CatalogExercise, WorkoutError>> {
    return this.createCustomExerciseUseCase.execute(input);
  }

  updateCustomExercise(
    input: UpdateCustomExerciseInput,
  ): Promise<Result<CatalogExercise, WorkoutError>> {
    return this.updateCustomExerciseUseCase.execute(input);
  }

  deleteCustomExercise(id: ExerciseId): Promise<Result<void, WorkoutError>> {
    return this.deleteCustomExerciseUseCase.execute(id);
  }

  getFocusModePreference(): Promise<Result<FocusModePreference>> {
    return this.getFocusModePreferenceUseCase.execute();
  }

  setFocusModePreference(preference: FocusModePreference): Promise<Result<void>> {
    return this.setFocusModePreferenceUseCase.execute(preference);
  }

  getKeepScreenAwakePreference(): Promise<Result<KeepScreenAwakePreference>> {
    return this.getKeepScreenAwakePreferenceUseCase.execute();
  }

  setKeepScreenAwakePreference(preference: KeepScreenAwakePreference): Promise<Result<void>> {
    return this.setKeepScreenAwakePreferenceUseCase.execute(preference);
  }
}
